import { Icon } from "@iconify/react";
import { Dialog, DialogProps } from "@mui/material";
import { toast } from "react-toastify";
import { useBulkCompleteResignation } from "../../queries/employees";
import { ButtonPrimary, ButtonSecondary, HStack, VStack } from "../utils";

export type EmployeeForResignation = {
  id: string;
  employeeName: string;
  allGrants?: { identifier: string; state: string }[];
};

export type BulkCompleteResignationDialogProps = {
  employees: EmployeeForResignation[];
  onClose: () => void;
  onSecondaryAction: () => void;
} & DialogProps;

function BulkCompleteResignationDialog({
  employees,
  onClose,
  onSecondaryAction,
  ...dialogProps
}: BulkCompleteResignationDialogProps) {
  const { mutate: bulkCompleteResignation, isLoading } =
    useBulkCompleteResignation();

  const employeesWithNonActiveGrants = employees
    .map((emp) => ({
      ...emp,
      filteredGrants: (emp.allGrants ?? []).filter(
        (g) => g.state !== "OFFERED" && g.state !== "GRANTED"
      ),
    }))
    .filter((e) => e.filteredGrants.length > 0);

  const hasNonActiveGrants = employeesWithNonActiveGrants.length > 0;

  function handleConfirm() {
    bulkCompleteResignation(
      employees.map((e) => e.id),
      {
        onSuccess: () => {
          toast("Resignations completed successfully!", {
            type: "success",
            autoClose: 2000,
          });
          onClose();
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.reason ||
              err?.response?.data?.errorRaw ||
              "Something went wrong",
            { type: "error", autoClose: 5000 }
          );
        },
      }
    );
  }

  return (
    <Dialog {...dialogProps} maxWidth="md">
      <VStack className="justify-between p-9 bg-white rounded min-w-[480px] max-w-[640px]">
        <VStack className="items-center justify-center gap-6 grow">
          <Icon icon="ep:warning" color="#FFE99B" height={72} />
        </VStack>
        <p className="items-center justify-center gap-4 font-medium text-center text-dark grow text-xs3 py-4">
          {`Are you sure you want to complete resignation for ${
            employees.length
          } employee${employees.length !== 1 ? "s" : ""}?`}
        </p>
        {hasNonActiveGrants && (
          <VStack className="w-full gap-2 mb-2">
            <div className="flex items-start gap-2 bg-[#FFFBEB] border border-[#FDE68A] rounded p-3">
              <Icon
                icon="ep:warning"
                color="#D97706"
                height={16}
                className="mt-0.5 shrink-0"
              />
              <span className="italic text-xs text-[#92400E]">
                The following grants were either not in a granted state or not
                accepted, and will be deleted upon completing resignations.
              </span>
            </div>
            <table className="w-full border-collapse mt-1">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Employee
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Grant Info
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Grant State
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Effect on Grant
                  </th>
                </tr>
              </thead>
              <tbody>
                {employeesWithNonActiveGrants.flatMap((emp) =>
                  emp.filteredGrants.map((grant, gi) => (
                    <tr
                      key={`${emp.id}-${grant.identifier}-${gi}`}
                      className="border-b border-gray-100"
                    >
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {gi === 0 ? emp.employeeName : ""}
                      </td>
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {grant.identifier}
                      </td>
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {grant.state}
                      </td>
                      <td className="text-xs px-3 py-2 italic text-gray-500">
                        Grant will be deleted
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </VStack>
        )}
        <HStack className="justify-between gap-4 pt-4">
          <ButtonSecondary
            onClick={onSecondaryAction}
            className="text-red-500 border-red-500"
          >
            Cancel
          </ButtonSecondary>
          <ButtonPrimary onClick={handleConfirm} loading={isLoading}>
            Confirm
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Dialog>
  );
}

export default BulkCompleteResignationDialog;
